import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts';

interface Prompt {
    id: string;
    title: string;
    title_es?: string;
    description: string;
    description_es?: string;
    votes: number;
    total_views: number;
    author_name?: string;
    author_profile_picture?: string;
    categories?: string[];
}

interface PublicPromptCardProps {
    prompt: Prompt;
}

const PublicPromptCard: React.FC<PublicPromptCardProps> = ({ prompt }) => {
    const navigate = useNavigate();
    const { i18n, t } = useLanguage();

    // Use localized content based on current language
    const title = i18n.language === 'es' && prompt.title_es ? prompt.title_es : prompt.title;
    const description = i18n.language === 'es' && prompt.description_es ? prompt.description_es : prompt.description;

    // Format author name to show first name and last initial
    const formattedAuthorName = () => {
        if (!prompt.author_name) return '';
        const parts = prompt.author_name.trim().split(/\s+/);
        if (parts.length === 1) return parts[0];
        return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
    };

    // Generate initials for avatar placeholder
    const authorInitials = prompt.author_name
        ?.split(' ')
        .map(item => item.charAt(0).toUpperCase())
        .join('') || 'AN';

    // Handle card click to redirect to login
    const handleCardClick = () => {
        navigate('/auth', {
            state: {
                message: t('loginToViewPromptDetails') || 'Login to view full prompt details',
                redirectTo: `/prompts/${prompt.id}`
            }
        });
    };

    const handleVoteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate('/auth', {
            state: {
                message: t('loginToVotePrompts') || 'Login to vote on prompts'
            }
        });
    };

    return (
        <div
            className="bg-white rounded-lg shadow-md flex flex-col gap-3 px-4 sm:px-6 py-3 sm:py-4 w-full cursor-pointer hover:shadow-lg transition-shadow duration-200"
            onClick={handleCardClick}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleCardClick();
                }
            }}
            aria-label={`${title} - ${t('clickToViewDetails')}`}
        >

            <div className="flex items-center">
                <h2 className="text-base sm:text-lg font-semibold text-brand-dark-blue leading-tight hover:text-brand-electric-blue transition-colors duration-200">
                    {title}
                </h2>
            </div>


            <p className="text-xs sm:text-sm text-left text-gray-600 leading-relaxed line-clamp-3">
                {description}
            </p>


            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between border-t border-gray-200 pt-3 gap-3 sm:gap-4">

                <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">

                    <button
                        onClick={handleVoteClick}
                        className="flex items-center bg-brand-light-gray rounded-full px-2 sm:px-3 py-1 text-brand-dark-blue text-xs sm:text-sm font-medium hover:bg-gray-200 transition-colors"
                        aria-label={t('loginToVote')}
                    >
                        <span className="mr-1">↑</span>
                        <span className="font-semibold">{prompt.votes} {t('votes')}</span>
                    </button>


                    <div className="flex items-center text-gray-500 text-xs sm:text-sm">
                        <svg
                            className="w-4 h-4 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                        </svg>
                        <span>{prompt.total_views} {t('views')}</span>
                    </div>


                    {prompt.categories && prompt.categories.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {prompt.categories.slice(0, 3).map((category, index) => (
                                <span
                                    key={`${category}-${index}`}
                                    className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium"
                                >
                                    {category}
                                </span>
                            ))}
                            {prompt.categories.length > 3 && (
                                <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full font-medium">
                                    +{prompt.categories.length - 3}
                                </span>
                            )}
                        </div>
                    )}
                </div>


                {prompt.author_name && (
                    <div className="w-full sm:w-auto flex justify-start sm:justify-end">
                        <div className="flex items-center gap-2">
                            {prompt.author_profile_picture ? (
                                <img
                                    src={prompt.author_profile_picture}
                                    alt={prompt.author_name}
                                    className="w-8 h-8 rounded-full object-cover border border-gray-200"
                                    onError={(e) => {
                                        // Fallback to initials if image fails to load
                                        e.currentTarget.style.display = 'none';
                                        const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                        if (fallback) fallback.style.display = 'flex';
                                    }}
                                />
                            ) : null}
                            <div
                                className={`w-8 h-8 rounded-full bg-brand-electric-blue text-white flex items-center justify-center text-xs font-semibold ${prompt.author_profile_picture ? 'hidden' : 'flex'
                                    }`}
                                aria-label={`${prompt.author_name} avatar`}
                            >
                                {authorInitials}
                            </div>
                            <span className="text-sm text-gray-700 font-medium">
                                {formattedAuthorName()}
                            </span>
                        </div>
                    </div>
                )}
            </div>


            <div className="text-xs text-gray-400 text-center pt-2 border-t border-gray-100">
                🔒 {t('clickToLogin') || 'Click to login and view full details'}
            </div>
        </div>
    );
};

export default React.memo(PublicPromptCard);
